<script lang="ts" setup>

export interface FormFieldsProps {
  fieldNamePrefix?: string
  isLocked?: boolean
  /** Priznak, ci sa maju v popise supisneho a orientacneho cisla uvadzat texty so zmienkou obcianskeho preukazu. */
  hintPersonalInformation?: boolean
  skPredefined?: boolean
  krajSelection?: undefined
  okresSelection?: undefined
  obecSelection?: undefined
  ulicaSelection?: undefined
}
import { useI18n } from 'vue-i18n'
import { useDynamicAddress } from '../composable/useDynamicAddressPartneri'
const props = withDefaults(defineProps<FormFieldsProps>(), {
  fieldNamePrefix: undefined,
  hintPersonalInformation: false,
  skPredefined: false,
  krajSelection: undefined,
  okresSelection: undefined,
  obecSelection: undefined,
  ulicaSelection: undefined,
})

const emit = defineEmits(['update-psc', 'update-ulica'])

const { t } = useI18n()
const { stat, kraj, okres, obec, ulica, psc, isSkSelected } = useDynamicAddress(
  props?.fieldNamePrefix
)

const setPSC = (value: any) => {
  if (value) {
    emit('update-psc', value.id)
  }
}

const setUlica = (value: any) => {
  if (value) {
    emit('update-ulica', value.id)
  }
}

const addNewUlica = (value: any) => {
  // Check if the value already exists in the array
  const isDuplicate = ulica.value.some((obj) => obj.id === value || obj.nazov === value)

  if (!isDuplicate) {
    ulica.value.push({ id: value, nazov: value })
  }
}

const addNewPsc = (value: any) => {
  psc.value.push({ id: value, nazov: value })
}
</script>
<template>
  <ValidationWrapper
    v-slot="slotProps"
    :name="`${fieldNamePrefix}.stat`"
    :value="props.skPredefined ? { id: 210, nazov: 'Slovenská republika' } : ''"
  >
    <IDSKSelect
      :id="`${fieldNamePrefix}.stat`"
      v-bind="slotProps"
      :label="t('globalne.stat')"
      full-width
      :options="stat"
      :disabled="isSkSelected && isLocked"
    />
  </ValidationWrapper>
  <template v-if="isSkSelected">
    <ValidationWrapper v-slot="slotProps" :name="`${fieldNamePrefix}.kraj`" :value="krajSelection">
      <IDSKSelect
        :id="`${fieldNamePrefix}.kraj`"
        v-bind="slotProps"
        :label="t('globalne.samospravnyKraj')"
        full-width
        :options="kraj"
        :disabled="!kraj?.length"
      />
    </ValidationWrapper>
    <ValidationWrapper v-slot="slotProps" :name="`${fieldNamePrefix}.okres`" :value="okresSelection">
      <IDSKSelect
        :id="`${fieldNamePrefix}.okres`"
        v-bind="slotProps"
        :label="t('globalne.okres')"
        full-width
        :options="okres"
        :disabled="!okres?.length"
      />
    </ValidationWrapper>
  </template>
  <ValidationWrapper
    v-if="isSkSelected"
    v-slot="slotProps"
    :name="`${fieldNamePrefix}.obec`"
    :value="obecSelection"
  >
    <IDSKSelect
      :id="`${fieldNamePrefix}.obec`"
      v-bind="slotProps"
      :label="t('globalne.obec')"
      full-width
      :options="obec"
      :disabled="!obec?.length"
    />
  </ValidationWrapper>
  <ValidationWrapper
    v-else
    v-slot="slotProps"
    :name="`${fieldNamePrefix}.obecZahranicie`"
    value=""
  >
    <IDSKInput
      :id="`${fieldNamePrefix}.obecZahranicie`"
      v-bind="slotProps"
      :label="t('globalne.obec')"
    />
  </ValidationWrapper>
  <ValidationWrapper v-slot="slotProps" :name="`${fieldNamePrefix}.ulica`" >
    <IDSKSelect
      v-if="isSkSelected"
      :id="`${fieldNamePrefix}.ulica`"
      v-bind="slotProps"
      :label="t('globalne.ulica')"
      full-width
      :option-object-label="'id'"
      :option-object-value="'nazov'"
      :options="ulica"
      :create-new-options="true"
      @update:model-value="(value) => setUlica(value)"
      @create-option="addNewUlica"
    />
    <IDSKInput
      v-else
      :id="`${fieldNamePrefix}.ulica`"
      v-bind="slotProps"
      :label="t('globalne.ulica')"
    />
  </ValidationWrapper>

  <ValidationWrapper
    v-slot="slotProps"
    :name="`${fieldNamePrefix}.supisneCislo`"
    value=""
  >
    <IDSKInput
      :id="`${fieldNamePrefix}.supisneCislo`"
      v-bind="slotProps"
      :label="t('globalne.supisneCislo')"
      :hint="
        props.hintPersonalInformation
          ? t('globalne.supisneCisloHintFyzickaOsoba')
          : t('globalne.supisneCisloHint')
      "
      class="govuk-input--width-20"
    />
  </ValidationWrapper>
  <ValidationWrapper
    v-slot="slotProps"
    :name="`${fieldNamePrefix}.orientacneCislo`"
    value=""
  >
    <IDSKInput
      :id="`${fieldNamePrefix}.orientacneCislo`"
      v-bind="slotProps"
      :label="t('globalne.orientacneCislo')"
      :hint="
        props.hintPersonalInformation
          ? t('globalne.orientacneCisloHintFyzickaOsoba')
          : t('globalne.orientacneCisloHint')
      "
      class="govuk-input--width-20"
    />
  </ValidationWrapper>

  <ValidationWrapper v-slot="slotProps" :name="`${fieldNamePrefix}.psc`" value="">
    <IDSKSelect
      v-if="isSkSelected"
      :id="`${fieldNamePrefix}.psc`"
      v-bind="slotProps"
      :label="t('globalne.psc')"
      full-width
      :options="psc"
      :option-object-label="'nazov'"
      :option-object-value="'id'"
      :create-new-options="true"
      @create-option="addNewPsc"
      @update:model-value="(value) => setPSC(value)"
    />
    <IDSKInput
      v-else
      :id="`${fieldNamePrefix}.psc`"
      v-bind="slotProps"
      :label="t('globalne.psc')"
      class="govuk-input--width-20"
    />
  </ValidationWrapper>

  <template v-if="!isSkSelected">
    <ValidationWrapper
      v-slot="slotProps"
      :name="`${fieldNamePrefix}.dalsieInformacie`"
      value=""
    >
      <IDSKInput
        :id="`${fieldNamePrefix}.dalsieInformacie`"
        v-bind="slotProps"
        :label="'Ďalšie informácie'"
      />
    </ValidationWrapper>

    <ValidationWrapper
      v-slot="slotProps"
      :name="`${fieldNamePrefix}.okresZahranicie`"
      value=""
    >
      <IDSKInput
        :id="`${fieldNamePrefix}.okresZahranicie`"
        v-bind="slotProps"
        :label="'Okres'"
      />
    </ValidationWrapper>
    <ValidationWrapper
      v-slot="slotProps"
      :name="`${fieldNamePrefix}.krajZahranicie`"
      value=""
    >
      <IDSKInput
        :id="`${fieldNamePrefix}.krajZahranicie`"
        v-bind="slotProps"
        :label="'Samosprávny kraj'"
      />
    </ValidationWrapper>
  </template>
</template>

<style scoped></style>
